"use client";

import { m as motion } from "framer-motion";
import { Lightbulb, Rocket, Map } from "lucide-react";

const personas = [
  {
    icon: <Lightbulb className="w-8 h-8" />,
    question: "Do you have a vision for better HR, but limited bandwidth?",
    description:
      "You have a long list of 'one day' HR projects, but administrative tasks keep getting in the way. You've found plenty of areas to improve people operations — there just aren't enough hours in the day. You're curious how AI can help.",
  },
  {
    icon: <Rocket className="w-8 h-8" />,
    question: "Are you ready to move beyond basic AI use cases?",
    description:
      "You've experimented with ChatGPT and other AI tools, but you know there's more potential. You want a structured approach to implement AI effectively across your HR functions and get measurable results.",
  },
  {
    icon: <Map className="w-8 h-8" />,
    question: "Do you need a practical implementation roadmap?",
    description:
      "You're tired of theoretical AI discussions and vendor pitches. You want hands-on guidance, real examples, and a clear framework for implementing AI in ways that your leadership and teams will embrace.",
  },
];

const accentColors = [
  "border-t-primary",
  "border-t-secondary",
  "border-t-accent",
];

export const RightForYouSection = () => {
  return (
    <section className="container-class py-8 md:py-12 lg:py-16 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-mesh opacity-10 pointer-events-none" />

      <div className="section-inner relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl lg:text-5xl mb-4">
            <span className="bg-gradient-to-r from-[hsl(var(--primary))] via-[hsl(var(--secondary))] to-[hsl(var(--accent))] bg-clip-text text-transparent">
              Who is this for?
            </span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto">
            If any of these sound like you, you're in the right place.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {personas.map((persona, i) => (
            <motion.div
              key={persona.question}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: i * 0.1 }}
              className={`glass-card p-8 hover-lift group border-t-2 ${accentColors[i]}`}
            >
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 mb-5 text-primary group-hover:scale-110 transition-transform duration-300">
                {persona.icon}
              </div>
              <h3 className="text-lg md:text-xl font-bold mb-3">
                {persona.question}
              </h3>
              <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
                {persona.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
